import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, Gift } from "lucide-react";
import { GoogleSignInButton } from "@/components/auth/GoogleSignInButton";
import { FacebookSignInButton } from "@/components/auth/FacebookSignInButton";
import { LinkedInSignInButton } from "@/components/auth/LinkedInSignInButton";
import { AuthBrandPanel } from "@/components/auth/AuthBrandPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandMark } from "@/components/shared/BrandMark";
import { useAuth } from "@/contexts/AuthContext";
import { SEO } from "@/components/shared/SEO";
import { COUNTRIES, COUNTRIES_BY_CODE, detectCountry } from "@/lib/countries";
import { api } from "@/lib/api";

const REF_STORAGE_KEY = "sb_pending_referral_code";

export const Signup: React.FC = () => {
  const { user, signup, loading } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("US");
  const [phoneLocal, setPhoneLocal] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // OTP step: when server responds 428 verify_required, we ask the user for a code.
  const [otpStep, setOtpStep] = useState<null | "request" | "verify">(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpSending, setOtpSending] = useState(false);

  // Referral attribution — accept `?ref=CODE` and persist in localStorage so
  // the code survives across social-login redirects.
  const [referralCode, setReferralCode] = useState<string>("");
  const [referrerName, setReferrerName] = useState<string>("");
  const [inviteDays, setInviteDays] = useState<number>(0);
  useEffect(() => {
    const fromUrl = (searchParams.get("ref") || "").trim().toUpperCase();
    let code = fromUrl;
    if (!code) {
      try { code = (localStorage.getItem(REF_STORAGE_KEY) || "").toUpperCase(); } catch { /* ignore */ }
    } else {
      try { localStorage.setItem(REF_STORAGE_KEY, code); } catch { /* ignore */ }
    }
    if (!code) return;
    setReferralCode(code);
    api.post("/referrals/validate", { code })
      .then((r) => { setReferrerName(r.data.referrer_name || ""); setInviteDays(r.data.invite_bonus_days || 0); })
      .catch(() => { setReferralCode(""); try { localStorage.removeItem(REF_STORAGE_KEY); } catch { /* ignore */ }});
  }, [searchParams]);

  useEffect(() => {
    detectCountry().then((c) => setPhoneCountry(c.code)).catch(() => null);
  }, []);

  // Where should an already-authenticated user land? Freshly-signed-up
  // users have persona=null + profile_complete=false → /onboarding. All
  // others → /dashboard. Uses the same rule as the imperative signup
  // handler so the two paths cannot drift.
  if (!loading && user) {
    const to = (!user.persona && !user.profile_complete) ? "/onboarding" : "/dashboard";
    return <Navigate to={to} replace />;
  }

  const country = COUNTRIES_BY_CODE[phoneCountry] || COUNTRIES_BY_CODE["US"];
  const fullPhone = `${country.dial}${phoneLocal.replace(/[^0-9]/g, "")}`;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    const digits = phoneLocal.replace(/[^0-9]/g, "");
    if (digits.length < 6 || digits.length > 15) {
      toast.error("Please enter a valid mobile number (6–15 digits after the country code).");
      return;
    }
    setSubmitting(true);
    try {
      // If we're on the OTP verify step, mark the code as verified server-side FIRST,
      // then proceed with signup (which double-checks via is_recently_verified).
      if (otpStep === "verify") {
        try {
          await api.post("/otp/verify", { purpose: "signup", email, code: otpCode });
        } catch (e: any) {
          toast.error(e?.response?.data?.detail || "Invalid code");
          setSubmitting(false);
          return;
        }
      }
      const u = await signup(name, email, password, fullPhone, phoneCountry, referralCode || undefined, otpCode || undefined);
      try { localStorage.removeItem(REF_STORAGE_KEY); } catch { /* ignore */ }
      toast.success(`Welcome to SociBaba, ${name.split(" ")[0]}!`);
      nav(u && !u.persona && !u.profile_complete ? "/onboarding" : "/dashboard");
    } catch (err: any) {
      // Server tells us OTP is required — kick off the verify flow.
      if (err?.response?.status === 428 || err?.response?.data?.detail === "verify_required") {
        setSubmitting(false);
        setOtpSending(true);
        try {
          await api.post("/otp/request", { purpose: "signup", email, name });
          toast.success(`Verification code sent to ${email}`);
          setOtpStep("verify");
        } catch (e: any) {
          toast.error(e?.response?.data?.detail || "Couldn't send verification code");
        } finally {
          setOtpSending(false);
        }
        return;
      }
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  const resendOtp = async () => {
    setOtpSending(true);
    try {
      await api.post("/otp/request", { purpose: "signup", email, name });
      toast.success(`New code sent to ${email}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Couldn't resend");
    } finally { setOtpSending(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-[color:var(--sg-bg)] overflow-x-hidden">
      <SEO
        title="Sign up — free workspace"
        description="Create a free SociBaba workspace. Publish content, classify comments with AI, capture leads and discover market demand — no card required."
        keywords={["SociBaba signup","free social media dashboard","creator signup","AI social CRM signup"]}
        path="/signup"
      />
      {/* Left hero pane — SociBaba brand pull-content. */}
      <div className="lg:col-span-2">
        <AuthBrandPanel variant="signup" />
      </div>
      {/* Right form pane */}
      <div className="lg:col-span-3 flex flex-col justify-center p-6 lg:p-14 relative">
        <div className="w-full max-w-md mx-auto">
        <div className="mb-8">
          <Link to="/" data-testid="signup-logo" className="inline-flex" title="SociBaba — home">
            <BrandMark
              showIcon
              iconClassName="h-10 w-10 mr-2 inline-block align-middle dark:invert"
              className="font-display text-2xl font-bold tracking-tight inline-flex items-center"
            />
          </Link>
        </div>

        <div className="sg-label mb-3">Create workspace</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mb-2">
          Get social signals working for you.
        </h1>
        <p className="text-sm text-[color:var(--sg-muted)] mb-4">
          Free forever for solo creators. No card required.
        </p>

        {referralCode && (
          <div
            data-testid="signup-referral-banner"
            className="mb-6 flex items-start gap-3 rounded-xl border border-[color:var(--sb-saffron)]/60 bg-[color:var(--sb-saffron)]/5 p-3"
          >
            <Gift className="h-4 w-4 text-[color:var(--sb-saffron)] mt-0.5 shrink-0" />
            <div className="text-xs">
              <div className="font-semibold text-[color:var(--sg-fg)]">
                {referrerName ? `${referrerName} invited you` : "You've been invited"}
                {inviteDays > 0 && <> — <span className="text-[color:var(--sb-saffron)]">+{inviteDays} bonus trial days</span></>}
              </div>
              <div className="text-[color:var(--sg-muted)] mt-0.5">Applied via code <span className="font-mono font-semibold">{referralCode}</span></div>
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-5" data-testid="signup-form">
          <div className="flex flex-col items-center gap-2 py-1">
            <GoogleSignInButton mode="signup" />
            <div className="w-full max-w-[320px] space-y-2">
              <FacebookSignInButton mode="signup" />
              <LinkedInSignInButton mode="signup" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-[color:var(--sg-border)]" />
            <span className="text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)]">or with email</span>
            <div className="flex-1 h-px bg-[color:var(--sg-border)]" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
              Full name
            </Label>
            <Input
              data-testid="signup-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-11 bg-white"
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
              Email
            </Label>
            <Input
              data-testid="signup-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-11 bg-white"
              required
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
              Mobile number
            </Label>
            <div className="flex gap-2">
              <select
                data-testid="signup-country"
                value={phoneCountry}
                onChange={(e) => setPhoneCountry(e.target.value)}
                className="h-11 bg-white rounded-md border border-[color:var(--sg-border)] px-2 text-sm min-w-[130px]"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>{c.name} {c.dial}</option>
                ))}
              </select>
              <Input
                data-testid="signup-phone"
                type="tel"
                value={phoneLocal}
                onChange={(e) => setPhoneLocal(e.target.value)}
                placeholder="9876543210"
                className="h-11 bg-white flex-1"
                required
              />
            </div>
            <div className="text-[11px] text-[color:var(--sg-muted)]">
              We'll use this for account recovery. Full: <span className="font-mono">{fullPhone}</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-[color:var(--sg-muted)]">
              Password
            </Label>
            <Input
              data-testid="signup-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11 bg-white"
              minLength={6}
              required
            />
          </div>
          {otpStep === "verify" && (
            <div className="rounded-xl border border-[color:var(--sb-saffron,#F97316)]/40 bg-[color:var(--sb-saffron,#F97316)]/5 p-4" data-testid="signup-otp-step">
              <div className="text-xs font-bold uppercase tracking-widest text-[color:var(--sb-saffron,#F97316)] mb-1">Verify your email</div>
              <p className="text-sm text-[color:var(--sg-muted)] mb-3">We sent a 6-digit code to <strong>{email}</strong>. Enter it below to finish signup.</p>
              <Input
                data-testid="signup-otp-code"
                type="text" inputMode="numeric" maxLength={6} autoFocus
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="6-digit code"
                className="h-11 bg-white tracking-[0.4em] text-center text-lg font-mono"
              />
              <button
                type="button"
                data-testid="signup-otp-resend"
                onClick={resendOtp}
                disabled={otpSending}
                className="mt-2 text-xs text-[color:var(--sg-primary)] hover:underline disabled:opacity-50"
              >{otpSending ? "Sending…" : "Didn't get it? Resend"}</button>
            </div>
          )}
          <Button
            type="submit"
            data-testid="signup-submit"
            disabled={submitting || (otpStep === "verify" && otpCode.length !== 6)}
            className="w-full h-11 bg-[color:var(--sg-fg)] text-white hover:bg-black hover:text-white font-semibold"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : otpStep === "verify" ? "Verify & create workspace" : "Create workspace"}
          </Button>
        </form>

        <p className="text-sm text-[color:var(--sg-muted)] mt-6 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-[color:var(--sg-primary)] font-semibold hover:underline">
            Log in
          </Link>
        </p>

        <p className="text-xs text-[color:var(--sg-muted)] mt-6 text-center leading-relaxed">
          By creating an account, you agree to our{" "}
          <Link to="/terms" className="underline hover:text-[color:var(--sg-fg)]" data-testid="signup-terms-link">Terms of Service</Link> and{" "}
          <Link to="/privacy" className="underline hover:text-[color:var(--sg-fg)]" data-testid="signup-privacy-link">Privacy Policy</Link>.
        </p>
        </div>
      </div>
    </div>
  );
};
